import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { Image } from '@hivebrite/design-system/Image';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { linkControlledHoverEffect } from '@hivebrite/design-system/Theme';

import type { ConfigUrls } from 'hb-react/frontoffice/apps/Forum/types';
import type { Discussion } from 'hb-react/frontoffice/components/CardForum/types';
import { HtmlContent } from 'hb-react/shared/components/HtmlContent';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

interface DiscussionContentAndCoverProps {
  discussion: Discussion;
  configUrls: ConfigUrls;
}

const DiscussionContentAndCover = ({
  discussion,
  configUrls,
}: DiscussionContentAndCoverProps) => {
  const navigate = useNavigate();
  const {
    id,
    title,
    stripped_content: strippedContent,
    categories,
    cover_picture_url: coverPictureUrl,
    cover_picture_medium_url: coverPictureMediumUrl,
  } = discussion;

  const goToDiscussion = (event) => {
    event.stopPropagation();
    navigate(configUrls.discussionUrl(id));
  };

  return (
    <Box
      justifyContent="space-between"
      flexDirection={{ _: 'column', m: 'row' }}
      alignItems="flex-start"
    >
      <Box
        flexDirection="column"
        width="100%"
        maxWidth={{ _: '100%', m: '632px' }}
      >
        <HoverStyled
          onClick={goToDiscussion}
          onKeyDown={onEnterOrSpacePress(goToDiscussion)}
          tabIndex={0}
        >
          <TitleStyled as="h3">
            <Dotdotdot clamp={2}>{title}</Dotdotdot>
          </TitleStyled>
          <Box display="block" mb={5}>
            <TextStyled font="h4-regular" display="block">
              <Dotdotdot clamp={2}>
                <HtmlContent content={strippedContent} />
              </Dotdotdot>
            </TextStyled>
          </Box>
        </HoverStyled>
        {categories?.length > 0 && (
          <Box mb={6}>
            <PillsList
              pills={categories}
              seeLessLabel={s__('Forum|See less')}
              seeMoreLabel={s__('Forum|See all')}
            />
          </Box>
        )}
      </Box>
      {coverPictureUrl && (
        <WrapperImgStyled
          data-testid="forum-card-cover-picture"
          onClick={goToDiscussion}
        >
          <ImgStyled src={coverPictureMediumUrl || coverPictureUrl} alt="" />
        </WrapperImgStyled>
      )}
    </Box>
  );
};

export default DiscussionContentAndCover;

const ImgStyled = styled(Image).attrs({
  display: 'block',
  width: '100%',
})`
  cursor: pointer;
  aspect-ratio: 1.85;
  object-fit: cover;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const TextStyled = styled(Box).attrs({
  font: 'h4-regular',
  display: 'block',
})`
  white-space: pre-line;
  overflow-wrap: anywhere;
`;

const TitleStyled = styled(Box).attrs({ font: 'h2-semibold', mb: 2, mt: 2 })`
  overflow-wrap: anywhere;
`;

const HoverStyled = styled(Box).attrs({
  display: 'block',
})`
  :focus,
  :hover {
    cursor: pointer;
    ${TitleStyled} {
      ${linkControlledHoverEffect()};
    }
  }
`;

const WrapperImgStyled = styled(Box).attrs({
  maxWidth: { _: '100%', m: '293px', l: '216px', xl: '260px' },
  mr: { _: 0, xl: 12 },
  ml: { _: 0, m: 10, l: 12 },
  mb: { _: 4, m: 0 },
  width: '100%',
})``;
